import React from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Collapse,
  Col,
  Row,
} from "reactstrap";
import { ToastContainer } from "react-toastify";
import SimpleHeader from "@/components/Headers/SimpleHeader";
import DisplayPropertiesContainer from "./Properties/DisplayPropertiesContainer";
import AddPropertiesContainer from "./Properties/AddPropertiesContainer";
import UpdatePropertiesContainer from "./Properties/UpdatePropertiesContainer";
import UpdateOrderContainer from "./Properties/UpdateOrderContainer";
import { Label } from "./Properties/Label";
import type { SitePermission } from "@/services/userServices/useCurrentPermission";

/** The property a panel edits: `{}` until a row's Update / Label / Order is clicked. */
type PropertyToUpdate = { _id?: string; name?: string };

type ManagePropertiesViewProps = {
  permission: SitePermission;
  openedCollapses: string[];
  openUpdateCard: boolean;
  openLabel: boolean;
  openOrder: boolean;
  elemToUpdate: PropertyToUpdate;
  collapsesToggle: (collapse: string) => void;
  handleUpdate: (elem: PropertyToUpdate) => void;
  handleLabel: (elem: PropertyToUpdate) => void;
  handleOrder: (elem: PropertyToUpdate) => void;
};

const ManagePropertiesView = ({
  permission,
  openedCollapses,
  openUpdateCard,
  openLabel,
  openOrder,
  elemToUpdate,
  collapsesToggle,
  handleUpdate,
  handleLabel,
  handleOrder,
}: ManagePropertiesViewProps) => {
  return (
    <div>
      {/* The screen's one toast container, outside both collapsible panels. Each
          panel used to carry its own, and the one inside the collapsed panel
          rendered every toast a second, invisible time (SCRUM-1803). */}
      <div className="rna-wrapper">
        <ToastContainer />
      </div>
      <SimpleHeader name="Manage Properties" parentName="Categories" />

      {permission?.publish || permission?.edit ? (
        <div className="accordion">
          <Container className="mt--6" fluid>
            <Row>
              {/* Left: Available Properties */}
              <Col
                className={
                  openedCollapses.includes("displayCard") ? "col-md-10" : "col-md-2"
                }
              >
                <Card className="card-plain">
                  <CardHeader
                    className="text-center"
                    role="tab"
                    onClick={() => collapsesToggle("displayCard")}
                    aria-expanded={openedCollapses.includes("displayCard")}
                  >
                    <h3 className="mb-0">Available Properties</h3>
                  </CardHeader>
                  <Collapse role="tabpanel" isOpen={openedCollapses.includes("displayCard")}>
                    <CardBody>
                      <DisplayPropertiesContainer
                        handleUpdate={handleUpdate}
                        handleLabel={handleLabel}
                        handleOrder={handleOrder}
                      />
                    </CardBody>
                  </Collapse>
                </Card>
              </Col>

              {/* Right: Create / Update / Label / Order */}
              <Col
                className={
                  openedCollapses.includes("editCard") ? "col-md-10" : "col-md-2"
                }
              >
                <Card className="card-plain">
                  <CardHeader
                    className="text-center"
                    role="tab"
                    onClick={() => collapsesToggle("editCard")}
                    aria-expanded={openedCollapses.includes("editCard")}
                  >
                    <h3 className="mb-0">
                      {openUpdateCard ? (
                        <>
                          Update <span className="text-primary">{elemToUpdate?.name}</span>
                        </>
                      ) : openLabel ? (
                        <>
                          Label For{" "}
                          <span className="text-primary">{elemToUpdate?.name}</span>
                        </>
                      ) : openOrder ? (
                        <>
                          Order For{" "}
                          <span className="text-primary">{elemToUpdate?.name}</span>
                        </>
                      ) : (
                        "Create"
                      )}
                      <span> Property</span>
                    </h3>
                  </CardHeader>
                  <Collapse role="tabpanel" isOpen={openedCollapses.includes("editCard")}>
                    <div style={{ paddingBottom: "30px" }}>
                      <CardBody className="text-center">
                        {openUpdateCard ? (
                          <UpdatePropertiesContainer
                            key={elemToUpdate?._id}
                            propTpUpdate={elemToUpdate}
                          />
                        ) : openLabel ? (
                          <Label
                            propTpUpdate={elemToUpdate}
                          />
                        ) : openOrder ? (
                          <UpdateOrderContainer
                            propTpUpdate={elemToUpdate}
                          />
                        ) : (
                          <AddPropertiesContainer />
                        )}
                      </CardBody>
                    </div>
                  </Collapse>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      ) : (
        // Read-only fallback
        <Container className="mt--6" fluid>
          <Row>
            <Col className="col-md-12">
              <Card className="card">
                <CardHeader className="text-center">
                  <h3 className="mb-0">Available Properties</h3>
                </CardHeader>
                <CardBody>
                  <DisplayPropertiesContainer
                    handleUpdate={handleUpdate}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      )}
    </div>
  );
};

export default React.memo(ManagePropertiesView);
